import { useState } from "react";
import { X as XIcon } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PURSE_TYPES, DESIGNERS, US_STATES, PACKAGES } from "./constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JEWELRY_METALS = ["Silver", "Gold", "Surprise Me!"];
const JEWELRY_STYLES = ["Bold and Gaudy", "Simple and Clean", "Surprise Me!"];

const inputCls = "bg-black/50 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white h-11 font-montserrat text-sm placeholder:text-white/30";

export function PackageOrderModal({ packageId, onClose }) {
  const pkg = PACKAGES.find((p) => p.id === packageId);
  const [form, setForm] = useState({
    customer_name: "", email: "", phone: "",
    billing_address: "", billing_city: "", billing_state: "", billing_zip: "",
    favorite_color: "", purse_type: "", favorite_designer: "",
    jewelry_metal: "", jewelry_style: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  if (!pkg) return null;

  const submit = async (e) => {
    e.preventDefault();
    const required = ["customer_name", "email", "phone", "billing_address", "billing_city", "billing_state", "billing_zip"];
    for (const f of required) if (!form[f].trim()) { toast.error("Please fill all required fields"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders`, {
        ...form,
        billing_country: "United States",
        shipping_same_as_billing: true,
        package_id: pkg.id,
      });
      toast.success("Order received! Opening secure payment...");
      window.location.href = pkg.link;
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4" data-testid="package-order-modal">
      <form onSubmit={submit} className="glass-card border border-[#D4AF37]/30 rounded-2xl w-full max-w-xl relative flex flex-col" style={{ maxHeight: "92vh" }}>
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-white/40 hover:text-white z-10 bg-black/50 rounded-full p-1.5">
          <XIcon className="w-4 h-4" />
        </button>
        <div className="px-5 pt-5 pb-3 border-b border-white/5">
          <h2 className="font-playfair text-[#D4AF37] text-lg sm:text-xl">{pkg.label}</h2>
          <p className="font-montserrat text-white/40 text-[11px] mt-0.5">${pkg.price}</p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input data-testid="pkg-name" placeholder="Full name *" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className={inputCls} />
            <Input data-testid="pkg-email" type="email" placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
            <Input data-testid="pkg-phone" placeholder="Phone *" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls + " sm:col-span-2"} />
          </div>

          <p className="font-montserrat text-white/50 text-[10px] uppercase tracking-wider">Shipping address</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input placeholder="Street address *" value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} className={inputCls + " sm:col-span-2"} />
            <Input placeholder="City *" value={form.billing_city} onChange={(e) => set("billing_city", e.target.value)} className={inputCls} />
            <Select value={form.billing_state} onValueChange={(v) => set("billing_state", v)}>
              <SelectTrigger className={inputCls}><SelectValue placeholder="State *" /></SelectTrigger>
              <SelectContent>{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Zip *" value={form.billing_zip} onChange={(e) => set("billing_zip", e.target.value)} className={inputCls + " sm:col-span-2"} />
          </div>

          <p className="font-montserrat text-white/50 text-[10px] uppercase tracking-wider">Purse preferences</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input placeholder="Favorite color" value={form.favorite_color} onChange={(e) => set("favorite_color", e.target.value)} className={inputCls} />
            <Select value={form.purse_type} onValueChange={(v) => set("purse_type", v)}>
              <SelectTrigger className={inputCls}><SelectValue placeholder="Purse type" /></SelectTrigger>
              <SelectContent>{PURSE_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.favorite_designer} onValueChange={(v) => set("favorite_designer", v)}>
              <SelectTrigger className={inputCls + " sm:col-span-2"}><SelectValue placeholder="Favorite designer" /></SelectTrigger>
              <SelectContent>{DESIGNERS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <p className="font-montserrat text-white/50 text-[10px] uppercase tracking-wider">Jewelry preferences</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select value={form.jewelry_metal} onValueChange={(v) => set("jewelry_metal", v)}>
              <SelectTrigger className={inputCls}><SelectValue placeholder="Metal" /></SelectTrigger>
              <SelectContent>{JEWELRY_METALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.jewelry_style} onValueChange={(v) => set("jewelry_style", v)}>
              <SelectTrigger className={inputCls}><SelectValue placeholder="Style" /></SelectTrigger>
              <SelectContent>{JEWELRY_STYLES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <p className="font-montserrat text-white/50 text-[10px] uppercase tracking-wider">Additional information</p>
          <Textarea
            data-testid="pkg-notes"
            placeholder="Any extra details, allergies, special requests, or notes for Vicki Ann..."
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            className="bg-black/50 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white font-montserrat text-sm placeholder:text-white/30 min-h-[90px]"
          />
        </div>

        <div className="px-5 py-3 border-t border-white/5 flex justify-end gap-2 bg-black/30 rounded-b-2xl">
          <button type="button" onClick={onClose} className="px-5 py-2 rounded-full text-white/50 text-sm font-montserrat hover:text-white">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="pkg-pay-btn" className="btn-gold px-6 py-2 rounded-full text-sm font-montserrat disabled:opacity-50">
            {submitting ? "Submitting..." : `Pay $${pkg.price}`}
          </button>
        </div>
      </form>
    </div>
  );
}
